import { SpinnerWrapper } from '@wix/bookings-viewer-ui';
import { useSettings } from '@wix/tpa-settings/react';
import { withSlotsPlaceholders } from '@wix/widget-plugins-ooi';
import {
  useEnvironment,
  useExperiments,
  useTranslation,
  WidgetProps,
} from '@wix/yoshi-flow-editor';
import loadable from '@wix/yoshi-flow-editor/loadable';
import React, { useEffect, useRef, useState } from 'react';
import { SpinnerTypes } from 'wix-ui-tpa/cssVars';
import type { SchedulingLocationViewModel } from '../../../service-page-view-model/scheduling-location-view-model/schedulingLocationViewModel';
import type { SchedulingSectionViewModel } from '../../../service-page-view-model/scheduling-section-view-model/schedulingSectionViewModel';
import type { ServicePageViewModel } from '../../../service-page-view-model/servicePageViewModel';
import type { SchedulingTimezoneViewModel } from '../../../service-page-view-model/shceduling-timezone-view-model/schedulingTimezoneViewModel';
import { ISection, SectionTypes, SidebarPosition } from '../types';
import { BookButtonViewModelProvider } from './useBookButtonViewModel';
import { ServicePageActionsProvider } from './useServicePageActions';
import { classes, st } from './Widget.st.css';

import { ServicePageActions } from '../actions/actions';
import { useAddVisibilityClass } from '../hooks/useAddVisibilityClass';
import { useBfcacheNavigationReset } from '../hooks/useBfcacheNavigationReset';
import { useSectionVisibilityCheck } from '../hooks/useSectionVisibilityCheck';
import { useVisibilityCheck } from '../hooks/useVisibilityCheck';
import settingsParams from '../settingsParams';
import Body from './Body/Body';
import Contact from './Contact';
import Description from './Description';
import Details from './Details';
import { Dialog } from './Dialog/Dialog';
import EmptyState from './EmptyState';
import Header from './Header/Header';
import Plugin from './Plugin';
import Policy from './Policy/Policy';
import Section from './Section';
import Sidebar from './Sidebar/Sidebar';
import TitleAndTagline from './TitleAndTagline';
import { DimensionsProvider } from './useDimensions';
import UserMessage from './UserMessage/UserMessage';
import { useWidgetSections } from './useWidgetSections';

const Gallery = loadable(
  () => import(/* webpackChunkName: "Gallery" */ './Gallery/Gallery'),
);
const Scheduling = loadable(
  () => import(/* webpackChunkName: "Scheduling" */ './Scheduling/Scheduling'),
);

export interface ControllerProps {
  viewModel?: ServicePageViewModel;
  scheduleViewModel?: SchedulingSectionViewModel;
  locationViewModel?: SchedulingLocationViewModel;
  timezoneViewModel?: SchedulingTimezoneViewModel;
  hasMoreEvents?: boolean;
  isFetchingMore?: boolean;
  serviceSlug?: string;
  actions?: ServicePageActions;
  userMessage?: {
    shouldDisplayMessage: boolean;
    closeMessage: () => void;
  };
  isRedirectingToCalendar?: boolean;
}
const getSectionComponentByType = (
  section: ISection,
  viewModel: ServicePageViewModel,
  shouldSectionBookButtonBeVisible: (sectionType: SectionTypes) => boolean,
  schedule?: SchedulingSectionViewModel,
  locationViewModel?: SchedulingLocationViewModel,
  timezoneViewModel?: SchedulingTimezoneViewModel,
  hasMoreEvents?: boolean,
  isFetchingMore?: boolean,
) => {
  switch (section.type) {
    case SectionTypes.TITLE_TAGLINE:
      return (
        <TitleAndTagline
          key={section.type}
          section={section}
          viewModel={viewModel.titleAndTagline}
        />
      );
    case SectionTypes.SCHEDULING:
      if (viewModel.showSchedulingSection) {
        return (
          <Section
            key={section.type}
            sectionType={SectionTypes.SCHEDULING}
            data-hook="scheduling-section"
            withBookButton={
              schedule?.isBookable &&
              shouldSectionBookButtonBeVisible(SectionTypes.SCHEDULING)
            }
          >
            <Scheduling
              key={section.type}
              schedule={schedule}
              locationViewModel={locationViewModel}
              timezoneViewModel={timezoneViewModel}
              hasMoreEvents={hasMoreEvents}
              isFetchingMore={isFetchingMore}
            />
          </Section>
        );
      }
      break;
    case SectionTypes.POLICY:
      return <Policy key={section.type} viewModel={viewModel.policySection} />;
    case SectionTypes.DETAILS:
      return (
        <Details
          key={section.type}
          section={section}
          viewModel={viewModel.detailsSection}
        />
      );
    case SectionTypes.DESCRIPTION:
      return (
        <Description
          key={section.type}
          section={section}
          viewModel={viewModel.descriptionSection}
        />
      );
    case SectionTypes.CONTACT:
      return (
        <Contact
          key={section.type}
          section={section}
          viewModel={viewModel.contactSection}
        />
      );
    case SectionTypes.GALLERY:
      return viewModel.gallerySection.items.length ? (
        <Section sectionType={SectionTypes.GALLERY} key={section.type}>
          <Gallery viewModel={viewModel.gallerySection} />
        </Section>
      ) : null;
    case SectionTypes.PLUGIN:
      return <Plugin key={section.type} />;
    default:
      return null;
  }
};

export const Widget: React.FC<WidgetProps<ControllerProps>> = ({
  viewModel,
  scheduleViewModel,
  locationViewModel,
  timezoneViewModel,
  hasMoreEvents,
  isFetchingMore,
  serviceSlug,
  userMessage,
  isRedirectingToCalendar,
  actions,
}) => {
  const { t } = useTranslation();
  const { experiments } = useExperiments();
  const { isMobile, isRTL } = useEnvironment();
  const settings = useSettings();
  const widgetRef = useRef<HTMLDivElement>(null);
  const sections = useWidgetSections();
  const { isCssPerBreakpoint } = useEnvironment();
  const { addVisibilityClass } = useAddVisibilityClass();
  const { shouldBeVisible } = useVisibilityCheck();
  const { shouldSectionBookButtonBeVisible, shouldSectionBeVisible } =
    useSectionVisibilityCheck();
  const [waitingForServiceSlugExpired, setWaitingForServiceSlugExpired] =
    useState(false);

  useBfcacheNavigationReset({
    navigationInitiatedBy: viewModel?.button?.navigationInitiatedBy,
    resetNavigationInitiatedBy: actions?.resetNavigationInitiatedBy,
    experiments,
  });

  const getSectionByType = (sectionType: SectionTypes): ISection => {
    return sections.find(({ type }) => type === sectionType) as ISection;
  };

  const isSectionVisible = (section: ISection): boolean => {
    return isCssPerBreakpoint
      ? shouldSectionBeVisible(section.type)
      : shouldSectionBeVisible(section.type) &&
          (!shouldBeVisible('sidebarVisibility') ||
            section.type !== settings.get(settingsParams.sidebarSection));
  };

  const shouldShowOnlyImage =
    shouldBeVisible('headerImageVisibility') &&
    !shouldBeVisible('headerBookButtonVisibility') &&
    !shouldBeVisible('headerTitleVisibility');
  const isServiceWithoutImage = !viewModel?.header?.image;
  const shouldShowHeader =
    shouldBeVisible('headerVisibility') &&
    !(shouldShowOnlyImage && isServiceWithoutImage);

  const renderBody = (widgetViewModel) => (
    <Body viewModel={widgetViewModel.body}>
      {sections.map((section) =>
        isSectionVisible(section)
          ? getSectionComponentByType(
              section,
              widgetViewModel,
              shouldSectionBookButtonBeVisible,
              scheduleViewModel,
              locationViewModel,
              timezoneViewModel,
              hasMoreEvents,
              isFetchingMore,
            )
          : null,
      )}
    </Body>
  );

  const renderSideBar = (widgetViewModel) =>
    shouldBeVisible('sidebarVisibility') ? (
      <Sidebar
        sectionType={settings.get(settingsParams.sidebarSection)}
        className={addVisibilityClass(
          '',
          classes.sidebarVisibility,
          'sidebarVisibility',
        )}
      >
        {getSectionComponentByType(
          getSectionByType(settings.get(settingsParams.sidebarSection)),
          widgetViewModel,
          shouldSectionBookButtonBeVisible,
          scheduleViewModel,
          locationViewModel,
          timezoneViewModel,
          hasMoreEvents,
          isFetchingMore,
        )}
      </Sidebar>
    ) : null;

  useEffect(() => {
    actions?.reportPageLoaded?.({
      emptyState: !viewModel,
      message_to_user: viewModel?.body.messageType,
      platform: isMobile ? 'mobile' : '',
      containers: viewModel
        ? `body${shouldShowHeader ? ',header' : ''}${
            shouldBeVisible('sidebarVisibility') ? ',sidebar' : ''
          }`
        : '',
      sections: viewModel ? JSON.stringify(sections) : '',
    });
  }, [actions?.reportPageLoaded]);

  // if we wait for service slug more than 3 seconds, the sausage probably didn't appear, and we should show dummy data instead of loader, so Service Page can be edited.
  // if the sausage will appear, we will show the real data of the service selected in the sausage.
  useEffect(() => {
    setTimeout(() => setWaitingForServiceSlugExpired(true), 3000);
  }, []);

  const isSideBarOnTheRight = () =>
    settings.get(settingsParams.sidebarPosition) === SidebarPosition.RIGHT;
  const isNoHeaderAndTitleInSideBar = () => {
    const isSideBarVisible = shouldBeVisible('sidebarVisibility');
    const isSideBarSectionIsTitleAndTagline =
      settings.get(settingsParams.sidebarSection) ===
      SectionTypes.TITLE_TAGLINE;
    const isSideBarTitleVisible = shouldBeVisible('serviceTitleVisibility');
    return (
      !shouldShowHeader &&
      isSideBarVisible &&
      isSideBarSectionIsTitleAndTagline &&
      isSideBarTitleVisible
    );
  };
  const inverseRowBySideBarPositionAndNoHeaderLogic =
    (isNoHeaderAndTitleInSideBar() && isSideBarOnTheRight()) ||
    (!isNoHeaderAndTitleInSideBar() && !isSideBarOnTheRight());

  return (
    <ServicePageActionsProvider value={actions!}>
      <BookButtonViewModelProvider value={viewModel?.button}>
        <DimensionsProvider widgetRef={widgetRef}>
          <div
            className={st(classes.root, {
              sidebarPosition: settings.get(settingsParams.sidebarPosition),
              isMobile,
              isNoHeaderAndTitleInSideBar:
                inverseRowBySideBarPositionAndNoHeaderLogic,
              isNavigating: !!viewModel?.button?.navigationInitiatedBy,
            })}
            ref={widgetRef}
          >
            {viewModel ? (
              viewModel.waitingForServiceSlug &&
              !waitingForServiceSlugExpired ? (
                <SpinnerWrapper
                  data-hook="page-loader"
                  type={SpinnerTypes.slim}
                  isCentered
                  className={classes.spinner}
                />
              ) : (
                <>
                  <Dialog viewModel={viewModel.dialogViewModel} />
                  <UserMessage
                    isOpen={userMessage?.shouldDisplayMessage || false}
                    onRequestClose={userMessage?.closeMessage || (() => {})}
                    isMobile={isMobile}
                    message={t('app.widget.uou-messages.non.premium.message')}
                    okLabel={t('app.widget.uou-messages.non.premium.approve')}
                    isRTL={isRTL}
                  />
                  <div
                    className={classes.root}
                    data-hook="booking-service-page-wrapper"
                  >
                    {shouldShowHeader && (
                      <Header
                        viewModel={viewModel.header}
                        className={addVisibilityClass(
                          '',
                          classes.headerVisibility,
                          'headerVisibility',
                        )}
                      />
                    )}
                    <div className={classes.dynamicWrapper}>
                      {isNoHeaderAndTitleInSideBar() ? (
                        <>
                          {renderSideBar(viewModel)}
                          {renderBody(viewModel)}
                        </>
                      ) : (
                        <>
                          {renderBody(viewModel)}
                          {renderSideBar(viewModel)}
                        </>
                      )}
                    </div>
                  </div>
                </>
              )
            ) : (
              <EmptyState isRedirectingToCalendar={isRedirectingToCalendar} />
            )}
          </div>
        </DimensionsProvider>
      </BookButtonViewModelProvider>
    </ServicePageActionsProvider>
  );
};

export default withSlotsPlaceholders(Widget);
